import React, { useState, useEffect, useRef } from 'react';
import { Alert, Badge, Button, Card, Form, Spinner } from 'react-bootstrap';
import { Auth, API } from "aws-amplify";
import { CSVLink } from 'react-csv';
import moment from 'moment';
import ReactTable from "react-table";
import 'react-table/react-table.css';
import { downloadStudentImportXlsx } from "../../utils/studentImportExport";

const CSV_HEADERS = [
    { label: 'ID', key: 'transactionID' },
    { label: 'Date', key: 'transactionDate' },
    { label: 'Description', key: 'name' },
    { label: 'Amount', key: 'cost' }
];

const DATE_ADJUSTMENT = {
    Saturday: 0,
    Sunday: 1,
    Monday: 2,
    Tuesday: 3,
    Wednesday: 4,
    Thursday: 5,
    Friday: 6
};

const COLUMNS = [
    { Header: 'Road Test Date', accessor: 'date' },
    { Header: 'Sign-Up Date', accessor: 'signupDate' },
    { Header: 'Notes', accessor: 'note' },
    { Header: 'First Name', accessor: 'fName' },
    { Header: 'Last Name', accessor: 'lName' },
    { Header: 'Email', accessor: 'emailAddress' },
    { Header: 'Phone', accessor: 'phone' },
    { Header: 'Slot', accessor: 'slot' },
    { Header: 'Package', accessor: 'selectedPackage' },
    { Header: 'Permit Number', accessor: 'permitNo' },
    { Header: 'Date of Birth', accessor: 'dob' },
    { Header: 'Payment Status', accessor: 'paymentStatus' },
    { Header: 'Driving School', accessor: 'drivingSchool' },
    { Header: 'Source', accessor: 'source' }
];

export default function RoadTestDates() {
    const [fetchedData, setFetchedData] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [dateFilterMode, setDateFilterMode] = useState('testDate');
    const [yearOptions, setYearOptions] = useState([]);
    const [quickYear, setQuickYear] = useState('');
    const [dateError, setDateError] = useState(null);
    const [refreshStatus, setRefreshStatus] = useState(null);
    const [csvData, setCsvData] = useState(null);
    const [studentImportWarnings, setStudentImportWarnings] = useState([]);

    const reactTableRef = useRef(null);
    const csvLinkRef = useRef(null);

    useEffect(() => {
        setFromDate(moment().startOf('year').format('YYYY-MM-DD'));
        setToDate(moment().endOf('year').format('YYYY-MM-DD'));

        let year = moment().add(1, 'year').year();
        const options = [];
        while (year > 2017) {
            options.push(String(year));
            year--;
        }
        setYearOptions(options.reverse());
        setQuickYear(moment().format('YYYY'));
    }, []);

    const handleQuickYearChange = (year) => {
        setQuickYear(year);
        setFromDate(moment(year, 'YYYY').startOf('year').format('YYYY-MM-DD'));
        setToDate(moment(year, 'YYYY').endOf('year').format('YYYY-MM-DD'));
    };

    useEffect(() => {
        if (csvData !== null && csvLinkRef.current) {
            csvLinkRef.current.link.click();
            setLoading(false);
        }
    }, [csvData]);

    const getFilteredStudents = async () => {
        if (!fromDate || !toDate) {
            setDateError("Please select both a start and end date.");
            return;
        }
        if (moment(fromDate).isAfter(moment(toDate))) {
            setDateError("Start date must be on or before the end date.");
            return;
        }

        setFetchedData(true);
        setLoading(true);
        setDateError(null);
        setRefreshStatus(null);

        try {
            const session = await Auth.currentSession();
            const headers = {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: session.idToken.jwtToken
            };

            // The API only supports a year-prefix match, so the range is covered by
            // fetching each spanned year (by whichever field the toggle selects) and
            // trimming to the exact range client-side.
            const filterField = dateFilterMode === 'signupDate' ? 'timestamp' : 'date';
            const startYear = moment(fromDate).year();
            const endYear = moment(toDate).year();
            const years = [];
            for (let year = startYear; year <= endYear; year++) {
                years.push(String(year));
            }

            const yearResults = await Promise.all(
                years.map(year => API.post("APIGateway", "/v2getStudentsByFilter", { body: { [filterField]: year }, headers }))
            );

            const rangeStart = moment(fromDate, "YYYY-MM-DD");
            const rangeEnd = moment(toDate, "YYYY-MM-DD").endOf('day');

            let rows = yearResults.flat().filter(x => {
                const recordDate = dateFilterMode === 'signupDate'
                    ? moment(x.timestamp)
                    : moment(x.date, "YYYY-MM-DD", true);
                return recordDate.isValid()
                    && !recordDate.isBefore(rangeStart)
                    && !recordDate.isAfter(rangeEnd);
            });

            if (rows.length === 0) {
                setDateError("No students found for the selected date range.");
                setData([]);
                setLoading(false);
                return;
            }

            rows = rows.map(x => {
                try {
                    x.scheduledLesson = x.selectedPackage.includes("Road Test Only")
                        ? "N/A"
                        : String(x.scheduledLesson);
                } catch {
                    x.scheduledLesson = "Unknown";
                }
                x.signupDate = moment(x.timestamp).isValid() ? moment(x.timestamp).format("MM-DD-YYYY") : "";
                x.date = moment(x.date)
                    .add(DATE_ADJUSTMENT[x.weekendDate], "days")
                    .format("MM-DD-YYYY (dddd)");
                return x;
            });

            rows.sort((a, b) => b.date < a.date ? 1 : b.date > a.date ? -1 : 0);

            setData(rows);
            setRefreshStatus(`${rows.length} record${rows.length === 1 ? "" : "s"} loaded.`);
            setLoading(false);
        } catch (error) {
            console.log(error);
            setLoading(false);
            if (error.response?.status === 401) {
                setDateError("Authentication Failed. Please log back in.");
            } else {
                setDateError(
                    "There was an error loading student records. Please try again or contact your developer. " +
                    JSON.stringify(error.response)
                );
            }
        }
    };

    const handleFilterDownload = () => {
        setLoading(true);
        setCsvData(reactTableRef.current.getResolvedState().sortedData);
    };

    // Fills in totalProgram/paymentType/initalPayment so the xlsx export
    // reports the same numbers as the other Student Import downloads.
    const enrichStudentsForExport = (students) =>
        students.map((student) => {
            student.totalProgram = (student.fullPricing || [])
                .map((x) => x.price)
                .reduce((a, b) => a + b, 0);
            if (!student.paymentType) {
                student.paymentType = "Credit Card";
            }
            student.initalPayment = student.totalProgram;
            return student;
        });

    const dateFilterModeSuffix = dateFilterMode === 'signupDate' ? 'BySignUpDate' : 'ByRoadTestDate';

    const handleStudentImportXlsxDownload = () => {
        // sortedData rows only carry the table's own column values; the full
        // record (fullPricing, paymentType, mName, etc.) lives on _original.
        const sorted = reactTableRef.current.getResolvedState().sortedData.map((row) => row._original);
        const enriched = enrichStudentsForExport(sorted);
        const filename = fromDate + "_to_" + toDate + "_" + dateFilterModeSuffix + "_StudentImport.xlsx";
        const { warnings } = downloadStudentImportXlsx(enriched, filename);
        setStudentImportWarnings(warnings);
    };

    return (
        <div className="holder">
            {/* Alerts */}
            {dateError && (
                <Alert variant="warning" onClose={() => setDateError(null)} dismissible>
                    <strong>{dateError}</strong>
                </Alert>
            )}
            {refreshStatus && (
                <Alert variant="info" onClose={() => setRefreshStatus(null)} dismissible style={{ padding: "0.4rem 0.75rem" }}>
                    {refreshStatus}
                </Alert>
            )}

            {/* Page header */}
            <div style={{ marginBottom: "1.25rem" }}>
                <h3 style={{ marginBottom: "0.25rem" }}>Student Record Table</h3>
                <small className="text-muted">Select a date range to load all student records. You can filter and download the results as CSV.</small>
            </div>

            {/* Date range selector */}
            <Card style={{ marginBottom: "1.5rem" }}>
                <Card.Body>
                    <Form.Group style={{ marginBottom: "1rem" }}>
                        <Form.Label style={{ marginBottom: "0.25rem", display: "block" }}><strong>Filter By</strong></Form.Label>
                        <Form.Check
                            inline
                            type="radio"
                            id="dateFilterModeTestDate"
                            name="dateFilterMode"
                            label="Road Test Date"
                            checked={dateFilterMode === 'testDate'}
                            onChange={() => setDateFilterMode('testDate')}
                        />
                        <Form.Check
                            inline
                            type="radio"
                            id="dateFilterModeSignupDate"
                            name="dateFilterMode"
                            label="Sign-Up Date"
                            checked={dateFilterMode === 'signupDate'}
                            onChange={() => setDateFilterMode('signupDate')}
                        />
                    </Form.Group>
                    <div style={{ display: "flex", alignItems: "flex-end", gap: "0.75rem", flexWrap: "wrap" }}>
                        <Form.Group controlId="quickYear" style={{ margin: 0, minWidth: "140px" }}>
                            <Form.Label style={{ marginBottom: "0.25rem" }}><strong>Quick Select Year</strong></Form.Label>
                            <Form.Control
                                as="select"
                                name="quickYear"
                                onChange={e => handleQuickYearChange(e.target.value)}
                                value={quickYear}
                            >
                                <option value="" disabled>Custom range</option>
                                {yearOptions.map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                        <Form.Group controlId="fromDate" style={{ margin: 0, minWidth: "160px" }}>
                            <Form.Label style={{ marginBottom: "0.25rem" }}><strong>From {dateFilterMode === 'signupDate' ? '(Sign-Up)' : '(Road Test)'}</strong></Form.Label>
                            <Form.Control
                                type="date"
                                name="fromDate"
                                onChange={e => { setFromDate(e.target.value); setQuickYear(''); }}
                                value={fromDate}
                            />
                        </Form.Group>
                        <Form.Group controlId="toDate" style={{ margin: 0, minWidth: "160px" }}>
                            <Form.Label style={{ marginBottom: "0.25rem" }}><strong>To {dateFilterMode === 'signupDate' ? '(Sign-Up)' : '(Road Test)'}</strong></Form.Label>
                            <Form.Control
                                type="date"
                                name="toDate"
                                onChange={e => { setToDate(e.target.value); setQuickYear(''); }}
                                value={toDate}
                            />
                        </Form.Group>
                        <Button
                            variant="primary"
                            disabled={loading || !fromDate || !toDate}
                            onClick={getFilteredStudents}
                        >
                            {loading && !data.length
                                ? <><Spinner animation="border" size="sm" /> Loading…</>
                                : "Fetch Records"
                            }
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            {/* Results */}
            {fetchedData && (
                loading && data.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "3rem 0", color: "#6c757d" }}>
                        <Spinner animation="border" role="status" />
                        <div style={{ marginTop: "0.75rem" }}>Loading records…</div>
                    </div>
                ) : data.length > 0 ? (
                    <Card>
                        <Card.Header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem" }}>
                            <span>
                                <strong>Records</strong>
                                <Badge variant="secondary" style={{ marginLeft: "0.5rem" }}>{data.length}</Badge>
                            </span>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                                <small className="text-muted">Filters applied to the table below are reflected in the downloads.</small>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={loading}
                                    onClick={handleFilterDownload}
                                >
                                    {loading ? <><Spinner animation="border" size="sm" /> Preparing…</> : "Download CSV"}
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={loading}
                                    onClick={handleStudentImportXlsxDownload}
                                >
                                    Download Student Import File (.xlsx)
                                </Button>
                            </div>
                        </Card.Header>
                        {studentImportWarnings.length > 0 && (
                            <Alert
                                variant="warning"
                                dismissible
                                onClose={() => setStudentImportWarnings([])}
                                style={{ margin: "0.75rem" }}
                            >
                                <strong>The downloaded Student Import file has some rows that may need review:</strong>
                                <ul style={{ marginBottom: 0 }}>
                                    {studentImportWarnings.map((warning, index) => (
                                        <li key={index}>{warning}</li>
                                    ))}
                                </ul>
                            </Alert>
                        )}
                        <Card.Body style={{ padding: 0 }}>
                            <CSVLink
                                ref={csvLinkRef}
                                data={csvData || []}
                                headers={CSV_HEADERS}
                                className="hidden"
                                filename={fromDate + "_to_" + toDate + "_" + dateFilterModeSuffix + "_parkwaydrivingschoolexport.csv"}
                            />
                            <ReactTable
                                ref={reactTableRef}
                                defaultSorted={[{ id: "transactionDate", desc: false }]}
                                data={data}
                                columns={COLUMNS}
                                minRows={0}
                                defaultPageSize={250}
                                pageSizeOptions={[5, 10, 20, 25, 50, 100, 250]}
                            />
                        </Card.Body>
                    </Card>
                ) : (
                    <Card body className="text-center text-muted">
                        No records found for {fromDate} to {toDate}.
                    </Card>
                )
            )}
        </div>
    );
}
